import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { RuntimeSessionAdapter } from './contracts';

const REFRESH_BUFFER_RATIO = 0.8;
const IDLE_THRESHOLD_MS = 5 * 60_000;
const IDLE_RECHECK_MS = 30_000;
const ACTIVITY_EVENTS: ReadonlyArray<keyof DocumentEventMap> = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
];
const ACTIVITY_THROTTLE_MS = 60_000;

interface TokenLifecycleOptions {
  session: RuntimeSessionAdapter;
  apiClient: AxiosInstance;
  onSessionExpired: () => void;
}

interface TokenLifecycleHandle {
  start: (accessToken: string, refreshToken?: string) => void;
  stop: () => void;
}

function base64UrlDecode(input: string): string {
  const standard = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = standard + '='.repeat((4 - (standard.length % 4)) % 4);
  return atob(padded);
}

function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(base64UrlDecode(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch (error) {
    void error;
    return null;
  }
}

function secondsUntilExpiry(token: string): number | null {
  const exp = decodeJwtExp(token);
  if (exp === null) return null;
  return exp - Math.floor(Date.now() / 1000);
}

export function createTokenLifecycle({
  session,
  apiClient,
  onSessionExpired,
}: TokenLifecycleOptions): TokenLifecycleHandle {
  let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  let currentRefreshToken: string | null = null;
  let isRefreshing = false;
  let refreshPromise: Promise<string | null> | null = null;
  let lastActivityTimestamp = Date.now();
  let activityThrottleTimer: ReturnType<typeof setTimeout> | null = null;
  let activityListenersAttached = false;
  let responseInterceptorId: number | null = null;
  let stopped = true;

  function trackActivity() {
    lastActivityTimestamp = Date.now();
  }

  const throttledTrackActivity = () => {
    if (activityThrottleTimer) return;
    trackActivity();
    activityThrottleTimer = setTimeout(() => {
      activityThrottleTimer = null;
    }, ACTIVITY_THROTTLE_MS);
  };

  function attachActivityListeners() {
    if (activityListenersAttached) return;
    for (const eventName of ACTIVITY_EVENTS) {
      document.addEventListener(eventName, throttledTrackActivity, { passive: true });
    }
    activityListenersAttached = true;
  }

  function detachActivityListeners() {
    if (!activityListenersAttached) return;
    for (const eventName of ACTIVITY_EVENTS) {
      document.removeEventListener(eventName, throttledTrackActivity);
    }
    activityListenersAttached = false;
  }

  function isUserActive(): boolean {
    const idleDuration = Date.now() - lastActivityTimestamp;
    return idleDuration < IDLE_THRESHOLD_MS;
  }

  async function performRefresh(): Promise<string | null> {
    if (stopped || !currentRefreshToken) return null;
    if (isRefreshing && refreshPromise) return refreshPromise;

    isRefreshing = true;
    refreshPromise = (async () => {
      try {
        const response = await apiClient.post<{
          accessToken: string;
          expiresIn: number;
          refreshToken?: string;
        }>('/auth/refresh', { refreshToken: currentRefreshToken });

        if (stopped) return null;

        const { accessToken, expiresIn, refreshToken: newRefreshToken } = response.data;
        session.setAuthToken(accessToken);

        if (newRefreshToken) {
          currentRefreshToken = newRefreshToken;
          session.setRefreshToken(newRefreshToken);
        }

        scheduleRefresh(accessToken, expiresIn);
        return accessToken;
      } catch (error) {
        console.error('[tokenLifecycle] Silent refresh failed', error);
        if (stopped) return null;
        currentRefreshToken = null;
        cancelScheduledRefresh();
        onSessionExpired();
        return null;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  function scheduleRefresh(accessToken: string, expiresIn?: number) {
    cancelScheduledRefresh();

    const remaining = secondsUntilExpiry(accessToken);
    const effectiveLifetime = remaining !== null && remaining > 0 ? remaining : expiresIn;
    if (!effectiveLifetime || effectiveLifetime <= 0) return;

    const delaySeconds = Math.floor(effectiveLifetime * REFRESH_BUFFER_RATIO);
    if (delaySeconds <= 0) return;

    const expiryTimestamp = Date.now() + effectiveLifetime * 1000;

    refreshTimerId = setTimeout(function attemptRefreshOrRecheck() {
      if (Date.now() >= expiryTimestamp) {
        onSessionExpired();
        return;
      }
      if (isUserActive()) {
        void performRefresh();
      } else {
        refreshTimerId = setTimeout(attemptRefreshOrRecheck, IDLE_RECHECK_MS);
      }
    }, delaySeconds * 1000);
  }

  function cancelScheduledRefresh() {
    if (refreshTimerId !== null) {
      clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
  }

  function install401Interceptor() {
    if (responseInterceptorId !== null) return;

    responseInterceptorId = apiClient.interceptors.response.use(undefined, async (error) => {
      const originalRequest = error.config as
        | (InternalAxiosRequestConfig & { _refreshRetried?: boolean })
        | undefined;

      if (
        error.response?.status !== 401 ||
        !originalRequest ||
        originalRequest._refreshRetried ||
        !currentRefreshToken
      ) {
        return Promise.reject(error);
      }

      const isRefreshCall = originalRequest.url?.includes('/auth/refresh');
      const isAuthMeCall = originalRequest.url?.includes('/auth/me');
      if (isRefreshCall || isAuthMeCall) {
        return Promise.reject(error);
      }

      originalRequest._refreshRetried = true;

      const newToken = await performRefresh();
      if (!newToken) {
        return Promise.reject(error);
      }

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(originalRequest);
    });
  }

  function uninstall401Interceptor() {
    if (responseInterceptorId !== null) {
      apiClient.interceptors.response.eject(responseInterceptorId);
      responseInterceptorId = null;
    }
  }

  function start(accessToken: string, refreshToken?: string) {
    stop();
    stopped = false;

    if (refreshToken) {
      currentRefreshToken = refreshToken;
    }

    if (!currentRefreshToken) return;

    lastActivityTimestamp = Date.now();
    attachActivityListeners();
    install401Interceptor();
    scheduleRefresh(accessToken);
  }

  function stop() {
    stopped = true;
    cancelScheduledRefresh();
    uninstall401Interceptor();
    detachActivityListeners();
    currentRefreshToken = null;
    isRefreshing = false;
    refreshPromise = null;
    if (activityThrottleTimer) {
      clearTimeout(activityThrottleTimer);
      activityThrottleTimer = null;
    }
  }

  return { start, stop };
}
